/**
 * Communications - Shared Type Definitions
 * Common types used across all communication channels
 */

// ============================================================================
// Communication Channel Types
// ============================================================================

export type CommunicationChannel = 'email' | 'sms' | 'whatsapp' | 'push';

export type CommunicationStatus =
  | 'Pending'
  | 'Processing'
  | 'Sent'
  | 'Delivered'
  | 'Failed';

// ============================================================================
// Channel Statistics
// ============================================================================

export interface ChannelStats {
  channel: CommunicationChannel;
  queuedCount: number;
  sentToday: number;
  failedToday: number;
  totalSent: number;
}

// ============================================================================
// Common Props Interfaces
// ============================================================================

export interface CommunicationPageProps {
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
}

// ============================================================================
// Helper Types
// ============================================================================

export interface Priority {
  value: number;
  label: string;
}

export const PRIORITIES: Priority[] = [
  { value: 1, label: '1 - Lowest' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5 - Normal' },
  { value: 6, label: '6' },
  { value: 7, label: '7' },
  { value: 8, label: '8' },
  { value: 9, label: '9' },
  { value: 10, label: '10 - Highest' },
];

// ============================================================================
// Status Helper Functions
// ============================================================================

export function getStatusColor(status: CommunicationStatus): 'success' | 'info' | 'warning' | 'danger' {
  switch (status) {
    case 'Delivered':
    case 'Sent':
      return 'success';
    case 'Processing':
      return 'info';
    case 'Pending':
      return 'warning';
    case 'Failed':
      return 'danger';
    default:
      return 'info';
  }
}

export function formatDateTime(date: Date | string | number | null | undefined): string {
  if (!date) return 'N/A';

  let d: Date;

  if (typeof date === 'number') {
    // Handle yyyyMMddHHmmss format (e.g., 20231225143000)
    const dateStr = date.toString();
    if (dateStr.length === 14) {
      const year = parseInt(dateStr.substring(0, 4), 10);
      const month = parseInt(dateStr.substring(4, 6), 10) - 1; // Month is 0-indexed
      const day = parseInt(dateStr.substring(6, 8), 10);
      const hour = parseInt(dateStr.substring(8, 10), 10);
      const minute = parseInt(dateStr.substring(10, 12), 10);
      const second = parseInt(dateStr.substring(12, 14), 10);
      d = new Date(year, month, day, hour, minute, second);
    } else {
      // Assume it's a Unix timestamp
      d = new Date(date);
    }
  } else if (typeof date === 'string') {
    d = new Date(date);
  } else {
    d = date;
  }

  if (!(d instanceof Date) || isNaN(d.getTime())) return 'N/A';

  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(date: Date | string | number | null | undefined): string {
  if (!date) return 'N/A';

  let d: Date;

  if (typeof date === 'number') {
    // Handle yyyyMMddHHmmss format (e.g., 20231225143000)
    const dateStr = date.toString();
    if (dateStr.length === 14) {
      const year = parseInt(dateStr.substring(0, 4), 10);
      const month = parseInt(dateStr.substring(4, 6), 10) - 1; // Month is 0-indexed
      const day = parseInt(dateStr.substring(6, 8), 10);
      const hour = parseInt(dateStr.substring(8, 10), 10);
      const minute = parseInt(dateStr.substring(10, 12), 10);
      const second = parseInt(dateStr.substring(12, 14), 10);
      d = new Date(year, month, day, hour, minute, second);
    } else {
      // Assume it's a Unix timestamp
      d = new Date(date);
    }
  } else if (typeof date === 'string') {
    d = new Date(date);
  } else {
    d = date;
  }

  if (!(d instanceof Date) || isNaN(d.getTime())) return 'N/A';

  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
